import { ROUTES } from '@/routes/routes';

/**
 * Notification Center — where a notification takes you when you click it.
 *
 * ── THE PROBLEM THIS SOLVES ────────────────────────────────────────────────
 * B6.17 delivered the read half of the Notification Center: the bell, the badge,
 * the list, mark-read. What it did not deliver was the *act* half. A row said
 * "Job Closed" or "Authorization Case Approved" and clicking it marked it read
 * and nothing else, so the Notification Center was an activity log rather than
 * an operational surface. The user still had to work out which job, open the
 * right list, and find it by number.
 *
 * ── WHERE THE DESTINATION COMES FROM ───────────────────────────────────────
 * From the notification's OWN event context — never from its title text.
 * `NotificationWorker` persists the whole serialized domain event as
 * `payloadJson` at creation time (`payload: data as unknown as Record<...>`), so
 * the entity ids are already on the wire. `NotificationController.toDto` already
 * exposes `payloadJson`. **No backend change was required and none was made.**
 *
 * Two payload shapes exist, and both are read here:
 *   - flat  — every `Inspection*`/`AuthorizationCase*`/`Report*` event sets its
 *             fields as top-level readonly props, so `inspectionId` /
 *             `authorizationId` serialize at the top level.
 *   - nested — `ProcedureStepAuthorization{Requested,Responded}Event` assign
 *             `this.payload = payload`, so the same ids arrive one level down.
 * `readId()` looks in both, in that order.
 *
 * ── ROUTES ARE NOT INVENTED HERE ───────────────────────────────────────────
 * Every destination below is an existing `ROUTES` entry that an existing screen
 * already navigates to for the same entity:
 *   - `INSPECTION_DETAIL`        — RecentActivityFeed, RequestCenterPage,
 *                                  ReportReviewsTable, the inspection lists.
 *   - `OPS_REVIEW_WORKSPACE`     — OperationsWorkQueuePage + OperationsReviewsTable,
 *                                  both under the same `roleSlug === 'operations-user'`
 *                                  branch this module reuses verbatim.
 *   - `AUTHORIZATION_CASE_DETAIL`— AuthorizationCenterPage, keyed by the case's
 *                                  `inspection_authorizations.id`, which is
 *                                  exactly the `authorizationId` the events carry.
 *
 * ── PERMISSIONS ────────────────────────────────────────────────────────────
 * A destination is only offered when the caller holds the permission its route
 * is gated on. That is a UX gate, not a security boundary: `PermissionRoute`
 * still guards both routes and the backend `PermissionGuard` is still the real
 * answer. Its purpose is the opposite of a bypass — a notification must never
 * become a click that lands the user on a redirect-to-dashboard, and must never
 * advertise an entity the user cannot open.
 *
 * All six default roles hold `inspection:read` and `inspection:authorization:read`
 * (default-roles.constants.ts), so under the seeded matrix every mapping below
 * resolves for every recipient. The checks exist because tenant roles are
 * editable, not because a seeded role fails them.
 *
 * ── FAIL SAFE ──────────────────────────────────────────────────────────────
 * `null` means "not actionable" and the row renders non-clickable. Every path
 * that cannot produce a *correct* destination returns `null` rather than a
 * plausible one: an unknown `type` (a future notification this build predates),
 * a missing or non-string id, a malformed payload, or a permission the caller
 * does not hold. Nothing here guesses from the title.
 */
export interface NotificationDestinationContext {
  /** From `useAuthStore(s => s.user?.roleSlug)`. */
  roleSlug: string | null | undefined;
  /** From `useHasPermission()`. */
  can: (permission: string) => boolean;
}

/** The fields of a notification this module reads. Nothing else is consulted. */
export interface NotificationDestinationInput {
  type: string;
  payloadJson: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Reads one id from a notification payload, tolerating both event shapes.
 * Returns `null` for anything that is not a non-empty string — a missing id must
 * make the row inert, never produce a URL with `undefined` in it.
 */
function readId(payloadJson: unknown, key: string): string | null {
  const top = asRecord(payloadJson);
  if (!top) return null;

  const direct = top[key];
  if (typeof direct === 'string' && direct.length > 0) return direct;

  const nested = asRecord(top.payload);
  const inner = nested?.[key];
  return typeof inner === 'string' && inner.length > 0 ? inner : null;
}

/** `/inspections/:id` — the generic detail page, gated on `inspection:read`. */
function inspectionDetail(
  payloadJson: unknown,
  ctx: NotificationDestinationContext,
): string | null {
  if (!ctx.can('inspection:read')) return null;
  const id = readId(payloadJson, 'inspectionId');
  return id ? ROUTES.INSPECTION_DETAIL.replace(':id', id) : null;
}

/**
 * The OPS review queue destination.
 *
 * Reuses the branch OperationsWorkQueuePage and OperationsReviewsTable already
 * apply to the same inspections: Operations gets the purpose-built Review
 * Workspace, every other role reachable here (Admin, Owner) keeps the generic
 * detail page. The extra `inspection:operations-review` check is not a
 * divergence from that precedent but a guard against its one failure mode —
 * `OPS_REVIEW_WORKSPACE` is `PermissionRoute`-gated with a *dashboard* fallback,
 * and bouncing a notification click onto a dashboard is exactly the outcome a
 * specific entity destination exists to prevent.
 */
function opsReviewOrInspectionDetail(
  payloadJson: unknown,
  ctx: NotificationDestinationContext,
): string | null {
  const id = readId(payloadJson, 'inspectionId');
  if (!id) return null;
  if (ctx.roleSlug === 'operations-user' && ctx.can('inspection:operations-review')) {
    return ROUTES.OPS_REVIEW_WORKSPACE.replace(':inspectionId', id);
  }
  return inspectionDetail(payloadJson, ctx);
}

/**
 * The canonical Request Thread — Stage C-vi.
 *
 * A modification notification names ONE request, and since Stage C that request
 * has a workspace of its own. The payload has carried `roundId` since D-131 M5
 * (`modificationPayload`: inspectionId, inspectionNumber, roundId, roundNumber —
 * ids and numbers only, never the customer's words), so this needed no backend
 * change and none was made.
 *
 * Before Stage C the click landed on the JOB — the best available answer when
 * the round had no address of its own, but it left the reader to find the
 * request among a job's ten sections. It now opens the request itself.
 *
 * The permission checked is the one the thread route and its API both require,
 * so a click can never land on a screen the caller will be refused. When it is
 * absent the old behaviour stands: the job detail, which every staff role can
 * read — and `null` rather than a plausible-looking URL when even that is not
 * available (design rule 10, unchanged).
 */
function requestThreadOrInspectionDetail(
  payloadJson: unknown,
  ctx: NotificationDestinationContext,
): string | null {
  const roundId = readId(payloadJson, 'roundId');
  if (roundId && ctx.can('inspection:modification:read')) {
    return ROUTES.REQUEST_THREAD.replace(':roundId', roundId);
  }
  return inspectionDetail(payloadJson, ctx);
}

/** `/authorization-center/:id`, keyed by `inspection_authorizations.id`. */
function authorizationCaseDetail(
  payloadJson: unknown,
  ctx: NotificationDestinationContext,
): string | null {
  if (!ctx.can('inspection:authorization:read')) return null;
  const id = readId(payloadJson, 'authorizationId');
  return id ? ROUTES.AUTHORIZATION_CASE_DETAIL.replace(':id', id) : null;
}

/**
 * Resolves the in-app path a staff notification should open, or `null` when the
 * notification has no destination the application can honestly offer.
 */
export function resolveNotificationDestination(
  notification: NotificationDestinationInput,
  ctx: NotificationDestinationContext,
): string | null {
  const { type, payloadJson } = notification;

  switch (type) {
    // ── The Operations review queue ─────────────────────────────────────────
    // Both of these put a job INTO the OPS review queue, which is the queue the
    // Review Workspace exists to work. Same destination, same role branch.
    case 'INSPECTION_SUBMITTED_FOR_REVIEW':
    case 'INSPECTION_COMMERCIAL_RETURN_ACKNOWLEDGED':
      return opsReviewOrInspectionDetail(payloadJson, ctx);

    // ── Decisions on, and changes to, one job ───────────────────────────────
    // Addressed to the assigned engineer (or broadcast to Commercial for the
    // customer-decision three). In every case the thing to open is the job.
    case 'INSPECTION_ASSIGNED':
    case 'INSPECTION_OPERATIONS_APPROVED':
    case 'INSPECTION_OPERATIONS_RETURNED':
    case 'INSPECTION_CLOSED':
    case 'INSPECTION_CANCELLED':
    case 'INSPECTION_CUSTOMER_APPROVED':
    case 'INSPECTION_CUSTOMER_REJECTED':
    case 'INSPECTION_CUSTOMER_MODIFICATION_REQUESTED':
      return inspectionDetail(payloadJson, ctx);

    // ── Service Report lifecycle ────────────────────────────────────────────
    // There is no report route. ReportReviewsTable states the reason and sets
    // the precedent this follows exactly: "the only action is Review, which
    // opens the inspection where the existing report workflow panel lives".
    case 'REPORT_SUBMITTED_FOR_REVIEW':
    case 'REPORT_APPROVED':
    case 'REPORT_REJECTED':
      return inspectionDetail(payloadJson, ctx);

    // ── Authorization cases ─────────────────────────────────────────────────
    // The exact entity is the case, and the case detail page is where it is
    // read and acted on (forward, record communication, release). It also
    // renders the originating inspection's context, so nothing is lost by
    // landing here rather than on the job.
    //
    // AUTHORIZATION_CASE_RELEASED is included deliberately. Its message ends
    // "you may resume work", which could argue for the inspection instead; the
    // case page is chosen because it is the exact entity named by the
    // notification and because splitting one entity across two destinations by
    // outcome is what makes a notification centre feel unpredictable. Flipping
    // it is a one-line change: move the label to the inspection group above.
    //
    // The two PROCEDURE_AUTH_* types belong to this group for a structural
    // reason: procedure-step authorization writes the same
    // `inspection_authorizations` row (notification.worker.ts, D-112 Stage 4:
    // "its cases are radar cases"), so their `authorizationId` addresses the
    // same page. That id sits one level down in their payload; `readId`
    // handles both shapes.
    case 'AUTHORIZATION_CASE_FORWARDED':
    case 'AUTHORIZATION_CASE_CUSTOMER_NOTIFIED':
    case 'AUTHORIZATION_CASE_CUSTOMER_RESPONDED':
    case 'AUTHORIZATION_CASE_RETURNED_TO_OPS':
    case 'AUTHORIZATION_CASE_RELEASED':
    case 'PROCEDURE_AUTH_REQUESTED':
    case 'PROCEDURE_AUTH_RESPONDED':
      return authorizationCaseDetail(payloadJson, ctx);

    // ── D-131 job-level modification rounds (M5) ────────────────────────────
    // The staff notifications the worker writes for a round. Their payload is
    // ids and numbers only (`modificationPayload`: inspectionId, inspectionNumber,
    // roundId, roundNumber — never the customer's words), so the click lands on
    // the job, where the round is read behind the permission gate. Operations
    // gets the Review Workspace (its Modification Request panel is the M5
    // surface); everyone else the generic detail page, exactly as the two OPS
    // review-queue types above. The staff detail page's own Modification panel
    // is M6, so Commercial/Admin land where the job already is.
    //
    // MODIFICATION_INFORMATION_NEEDED and QUOTATION_REVISION_SENT are written
    // with recipientType CUSTOMER only and never reach this resolver;
    // MODIFICATION_ROUND_CLOSED is written to both audiences and the staff copy
    // is handled here.
    case 'MODIFICATION_ROUND_OPENED':
    case 'MODIFICATION_OPS_REVIEW_REQUESTED':
    case 'MODIFICATION_OPS_VERDICT_RECORDED':
    case 'MODIFICATION_CUSTOMER_REPLIED':
    case 'MODIFICATION_ROUND_CLOSED':
    case 'QUOTATION_REVISION_DECIDED':
      // STAGE C-vi. These six are the only notifications that name a
      // MODIFICATION ROUND, and each now opens that round's own workspace
      // instead of the job it belongs to. Everything above is unchanged: an
      // inspection notification still opens the inspection, and the two OPS
      // review-queue types still open the whole-inspection Review Workspace,
      // because that is what they are about.
      return requestThreadOrInspectionDetail(payloadJson, ctx);

    // ── Deliberately not actionable ─────────────────────────────────────────
    // TENANT_PROVISIONED is platform news written into the tenant's staff
    // broadcast. Tenant provisioning is administered from the Platform Console,
    // a separate application behind a separate credential domain; the tenant
    // portal has no tenant page to open. Non-clickable rather than sent
    // somewhere approximate (design rule 10).
    case 'TENANT_PROVISIONED':
      return null;

    // INSPECTION_QUOTATION_SENT and REPORT_PUBLISHED are written with
    // recipientType CUSTOMER and are never returned by the staff endpoint —
    // `NotificationService.scopeFor` filters on `recipientType: 'STAFF'`. They
    // are handled by the Customer Portal resolver, not here.
    default:
      return null;
  }
}
